import { useEffect, useRef, useState, type MouseEvent, type ReactNode } from 'react'
import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import { cx } from '@/lib/cx'
import { useIsMobile } from '@/lib/mobile'
import styles from './SettingsLayer.module.css'

export interface FrameItem {
  id: string
  label: string
  icon: ReactNode
  /** Действие вместо перехода в раздел — «Выйти». */
  onClick?: () => void
  danger?: boolean
}

export interface FrameGroup {
  key: string
  title?: ReactNode
  items: FrameItem[]
}

interface Props {
  /** Подпись области для чтения с экрана. */
  label: string
  /** Заголовок списка разделов на телефоне. */
  listTitle: string
  closeLabel: string
  /** Карточка над разделами: профиль или сервер. С id — сама раздел («Редактировать профиль»). */
  head: { id?: string; node: ReactNode }
  groups: FrameGroup[]
  active: string
  onSelect: (id: string) => void
  onClose: () => void
  /** На телефоне начать со списка разделов: открыли из шестерёнки, а не ссылкой на раздел. */
  startWithList: boolean
  /** Подпункты открытого раздела из его заголовков h2. */
  anchors?: boolean
  children: ReactNode
}

/**
 * Каркас настроек — окно поверх приложения, как в Discord: слева разделы с иконками,
 * справа шапка с названием раздела и крестиком. Название берём из первого h1 раздела:
 * там бывает счётчик («Эмодзи — 3 из 50») или имя бота, которых нет в списке слева.
 * На телефоне — во весь экран: список разделов, тап открывает раздел, стрелка возвращает.
 */
export function SettingsFrame({ label, listTitle, closeLabel, head, groups, active, onSelect, onClose, startWithList, anchors: withAnchors = false, children }: Props) {
  const mobile = useIsMobile()
  const [listMode, setListMode] = useState(startWithList)
  const contentRef = useRef<HTMLDivElement>(null)
  const [title, setTitle] = useState('')
  const [anchors, setAnchors] = useState<string[]>([])
  // подсветку подпункта держим вместе с разделом: при переходе она сбрасывается сама
  const [here, setHere] = useState<{ section: string; i: number; pinned: boolean }>({ section: '', i: 0, pinned: false })
  const hereNow = here.section === active ? here.i : 0

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // открытое модальное окно закрывается само и останавливает событие
      if (e.key === 'Escape' && !document.querySelector('[role="dialog"][aria-modal="true"]')) onClose()
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])

  /*
   * Название и подпункты берём из заголовков самого раздела, а не из отдельного списка:
   * его пришлось бы держать в согласии с каждым разделом руками. Следим за содержимым —
   * разделы дорисовывают заголовки, когда приезжают данные.
   */
  useEffect(() => {
    const el = contentRef.current
    if (!el) return
    const read = () => {
      setTitle(el.querySelector('h1')?.textContent?.trim() ?? '')
      if (!withAnchors) return
      const next = [...el.querySelectorAll('h2')].map((h) => h.textContent?.trim() ?? '').filter(Boolean)
      setAnchors((prev) => (prev.length === next.length && prev.every((v, i) => v === next[i]) ? prev : next))
    }
    read()
    const observer = new MutationObserver(read)
    observer.observe(el, { childList: true, subtree: true, characterData: true })
    return () => observer.disconnect()
  }, [active, listMode, mobile, withAnchors])

  /*
   * Нажатый подпункт держим выбранным, пока человек сам не прокрутит. Последние заголовки
   * до верха не доезжают — ниже них нет содержимого, — и слежение перекидывало подсветку
   * на предыдущий: кликаешь по нижнему подпункту, а выбранным становится другой.
   */
  const onScroll = () => {
    const el = contentRef.current
    if (!el || !withAnchors) return
    const heads = [...el.querySelectorAll('h2')]
    if (heads.length === 0) return
    // докрутили до самого низа — значит мы у последнего заголовка, выше он не поднимется
    let i = heads.length - 1
    if (el.scrollTop + el.clientHeight < el.scrollHeight - 4) {
      const top = el.getBoundingClientRect().top + 80
      i = 0
      heads.forEach((h, n) => {
        if (h.getBoundingClientRect().top <= top) i = n
      })
    }
    setHere((h) => (h.pinned && h.section === active ? h : { section: active, i, pinned: false }))
  }

  // прокрутка руками снимает закрепление: дальше снова следим за заголовками
  const unpin = () => setHere((h) => (h.pinned ? { ...h, pinned: false } : h))

  const goTo = (i: number) => {
    setHere({ section: active, i, pinned: true })
    contentRef.current?.querySelectorAll('h2')[i]?.scrollIntoView({ block: 'start', behavior: 'smooth' })
  }

  const select = (id: string) => {
    onSelect(id)
    setListMode(false)
  }

  const items = groups.flatMap((g) => g.items)
  const barTitle = title || items.find((i) => i.id === active)?.label || ''

  const headNode = head.id ? (
    <button type="button" className={cx(styles.navHead, active === head.id && styles.navHeadActive)} onClick={() => select(head.id as string)} aria-current={active === head.id ? 'page' : undefined}>
      {head.node}
    </button>
  ) : (
    <div className={styles.navHead}>{head.node}</div>
  )

  // клик по подложке закрывает, как у остальных окон; нажали внутри и отпустили снаружи — нет
  const backdrop = (e: MouseEvent) => {
    if (e.target === e.currentTarget) onClose()
  }

  if (mobile && listMode) {
    return (
      <div className={styles.layer} role="region" aria-label={label}>
        <div className={styles.window}>
          <div className={styles.main}>
            <div className={styles.bar}>
              <span className={styles.barTitle}>{listTitle}</span>
              <button type="button" className={styles.barButton} onClick={onClose} aria-label={closeLabel}><X size={22} /></button>
            </div>
            <div className={cx(styles.mobileList, 'scroller')}>
              {head.id ? (
                <button type="button" className={styles.mobileHead} onClick={() => select(head.id as string)}>
                  {head.node}
                  <ChevronRight size={20} className={styles.mobileChevron} />
                </button>
              ) : (
                <div className={styles.mobileHead}>{head.node}</div>
              )}
              {groups.map((group) => (
                <div key={group.key} className={styles.mobileGroup}>
                  {group.title ? <span className={styles.navHeader}>{group.title}</span> : null}
                  <div className={styles.mobileRows}>
                    {group.items.map((item) => (
                      <button key={item.id} type="button" className={cx(styles.mobileRow, item.danger && styles.mobileRowDanger)} onClick={() => (item.onClick ? item.onClick() : select(item.id))}>
                        <span className={styles.navIcon} aria-hidden="true">{item.icon}</span>
                        <span className={styles.mobileRowLabel}>{item.label}</span>
                        {item.onClick ? null : <ChevronRight size={20} className={styles.mobileChevron} />}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.layer} role="region" aria-label={label} onMouseDown={backdrop}>
      <div className={styles.window}>
        {mobile ? null : (
          <nav className={cx(styles.sidebar, 'scroller')} aria-label="Разделы">
            {headNode}
            {groups.map((group) => (
              <div key={group.key} className={styles.navGroup}>
                {group.title ? <span className={styles.navHeader}>{group.title}</span> : null}
                {group.items.map((item) => {
                  const open = item.id === active
                  return (
                    <div key={item.id}>
                      <button
                        type="button"
                        className={cx(styles.navItem, open && styles.navItemActive, item.danger && styles.navItemDanger)}
                        onClick={() => (item.onClick ? item.onClick() : select(item.id))}
                        aria-current={open ? 'page' : undefined}
                      >
                        <span className={styles.navIcon} aria-hidden="true">{item.icon}</span>
                        <span className={styles.navLabel}>{item.label}</span>
                      </button>
                      {open && withAnchors && anchors.length > 1 ? (
                        <div className={styles.navAnchors}>
                          {anchors.map((a, i) => (
                            <button key={a} type="button" className={cx(styles.navAnchor, hereNow === i && styles.navAnchorActive)} onClick={() => goTo(i)}>
                              {a}
                            </button>
                          ))}
                        </div>
                      ) : null}
                    </div>
                  )
                })}
              </div>
            ))}
          </nav>
        )}
        <div className={styles.main}>
          <div className={styles.bar}>
            {mobile ? (
              <button type="button" className={cx(styles.barButton, styles.barBack)} onClick={() => setListMode(true)} aria-label="К списку разделов"><ChevronLeft size={26} /></button>
            ) : null}
            {/* h1 раздела остаётся в странице для чтения с экрана — здесь его видимая копия */}
            <span className={styles.barTitle} aria-hidden="true">{barTitle}</span>
            <button type="button" className={styles.barButton} onClick={onClose} aria-label={closeLabel} data-tooltip="Закрыть (Esc)"><X size={22} /></button>
          </div>
          {/* key — чтобы новый раздел открывался сверху, а не с прокруткой предыдущего */}
          <div key={active} className={cx(styles.content, 'scroller')} ref={contentRef} onScroll={onScroll} onWheel={unpin} onTouchMove={unpin} onKeyDown={unpin}>
            <div className={styles.page}>{children}</div>
          </div>
        </div>
      </div>
    </div>
  )
}
